"use client";
import { useState, useEffect, useRef } from "react";
import { FiSearch, FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Link from "next/link";

interface SearchResult {
  title: string;
  description: string;
  og_url: string | null;
}

const SearchComponent = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const RESULTS_PER_PAGE = 10;

  useEffect(() => {
    if (isSearchOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isSearchOpen]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchOpen]);

 
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setTotalResults(0);
      return;
    }

    const timerId = setTimeout(() => {
      performSearch();
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery, currentPage]);

  const performSearch = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://www.w3era.com/api/search-meta/?search=${encodeURIComponent(
          searchQuery
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ search: searchQuery }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        // console.log("API Response:", data);

        // Handle both array and object responses
        let results = Array.isArray(data) ? data : data.results || [];

        // Ensure all results have required fields
        const validResults = results
          .filter(
            (result: any) =>
              result.og_url &&
              typeof result.title === "string" &&
              typeof result.description === "string"
          )
          .map((result: any) => ({
            title: result.title,
            description: result.description,
            og_url: result.og_url,
          }));

        // console.log("Valid Results:", validResults);
        setSearchResults(validResults);
        setTotalResults(validResults.length); // Or use data.total if available
      } else {
        // console.error("Search failed:", response.status);
        setSearchResults([]);
        setTotalResults(0);
      }
    } catch (error) {
    //   console.error("Search error:", error);
      setSearchResults([]);
      setTotalResults(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setCurrentPage(1);
      setTotalResults(0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsSearchOpen(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= Math.ceil(totalResults / RESULTS_PER_PAGE)) {
      setCurrentPage(newPage);
    }
  };

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
  
    const regex = new RegExp(`(${query.split(' ').filter(q => q).join('|')})`, 'gi');
    return text.replace(regex, '<span class="text-[#1090D0]">$1</span>');
  };

  const renderResultLink = (result: SearchResult) => {
    if (!result.og_url) {
      return (
        <div className="block p-6 hover:bg-gray-50 border-b border-black last:border-b-2">
          <h3 
            className="text-lg font-semibold text-gray-900"
            dangerouslySetInnerHTML={{ 
              __html: highlightText(result.title, searchQuery) 
            }}
          />
          <p 
            className="text-gray-600 mt-2 line-clamp-2"
            dangerouslySetInnerHTML={{ 
              __html: highlightText(result.description, searchQuery) 
            }}
          />
          <div className="text-sm text-gray-400 mt-2">No URL available</div>
        </div>
      );
    }
  
    return (
      <Link
        href={result.og_url}
        target="_blank"
        className="block p-6 hover:bg-gray-50 border-b border-black last:border-b-2"
      >
        <h3 
          className="!text-[22px] md:!text-[24px] lg:!text-[30px] !font-[900] text-homeblack hover:text-blue-600"
          dangerouslySetInnerHTML={{ 
            __html: highlightText(result.title, searchQuery) 
          }}
        />
        <p 
          className="text-homegrey mt-2 line-clamp-2"
          dangerouslySetInnerHTML={{ 
            __html: highlightText(result.description, searchQuery) 
          }}
        />
        <div className="text-lg text-blue mt-2 truncate">
          {result.og_url}
        </div>
      </Link>
    );
  };

  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE);
  const startResult = (currentPage - 1) * RESULTS_PER_PAGE + 1;
  const endResult = Math.min(currentPage * RESULTS_PER_PAGE, totalResults);

  return (
    <div className="relative" ref={searchRef}>
      
      <button
        onClick={handleSearchToggle}
        className="text-white transition-colors duration-200 flex items-center gap-2"
        aria-label="Search"
      >
        <FiSearch className="w-5 h-5" />
      </button>

      
      {isSearchOpen && (
        <div className="fixed inset-0 z-[9999999]">
          
          <div className="bg-white w-full shadow-xl">
            <div className="container mx-auto px-4 py-4 flex items-center">
              <div className="relative w-full">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Start your search here"
                  className="w-full py-3 px-4 pr-12 text-2xl bg-white text-gray-800 focus:outline-none"
                  autoFocus
                />
                <button
                  onClick={handleSearchToggle}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <FiX className="w-8 h-8 font-bold" size={20} />
                </button>
              </div>
            </div>
          </div>

          
          <div className="bg-gray-100 h-screen pt-[72px]">
            <div className="container mx-auto px-4 h-full overflow-y-auto pb-20">
              {isLoading ? (
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="p-6 border-b border-gray-100">
                      <div className="animate-pulse">
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchResults.length > 0 ? (
                <>
                  <div className="mb-4 text-lg text-gray-500">
                    Showing <span className="text-homeblack">{startResult} - </span><span className="text-homeblack">{endResult}</span> out of <span className="text-homeblack">{totalResults}</span>{" "}
                    for: {searchQuery}
                  </div>
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden my-5">
                    {searchResults
                      .slice(
                        (currentPage - 1) * RESULTS_PER_PAGE,
                        currentPage * RESULTS_PER_PAGE
                      )
                      .map((result, index) => (
                        <div key={index}>{renderResultLink(result)}</div>
                      ))}
                  
                  {totalPages > 1 && (
                    <div className="flex justify-around mx-auto items-center md:my-8 mt-10 mb-[4.5rem]">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="flex items-center px-4 py-2 md:text-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiChevronLeft className="mr-2" />
                        Previous
                      </button>
                      <span className="md:text-lg text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="flex items-center px-4 py-2 md:text-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                        <FiChevronRight className="ml-2" />
                      </button>
                    </div>
                  )}
                  </div>
                </>
              ) : searchQuery && !isLoading ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    No results found
                  </h3>
                  <p className="mt-2 text-gray-600">
                    We couldn&apos;t find any matches for &quot;{searchQuery}&quot;
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchComponent;
